// app/components/Plugins/Automations/nodes/StartNode.tsx
'use client'

import React, { useState, useCallback } from 'react'
import { Handle, Position } from '@xyflow/react'
import { StartNodeData, NodeProps } from '../types'

const StartNode: React.FC<NodeProps<StartNodeData>> = ({ data, isConnectable }) => {
  // Local state only for the inputs
  const [tickers, setTickers] = useState(data.tickers || '')
  const [frequency, setFrequency] = useState(data.frequency || 'once')
  const [customSeconds, setCustomSeconds] = useState(data.customSeconds || 60)

  // Handlers to update node.data
  const handleTickersChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const v = evt.target.value
      setTickers(v)
      data.tickers = v
    },
    [data]
  )

  const handleFrequencyChange = useCallback(
    (evt: React.ChangeEvent<HTMLSelectElement>) => {
      const v = evt.target.value
      setFrequency(v)
      data.frequency = v
    },
    [data]
  )

  const handleCustomSecondsChange = useCallback(
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseInt(evt.target.value, 10) || 0
      setCustomSeconds(v)
      data.customSeconds = v
    },
    [data]
  )

  // **No local countdown/state**: read straight from data
  const countdown = data.countdown ?? 0
  const isRunning = !!data.isRunning

  // Format MM:SS
  const formatCountdown = useCallback(() => {
    const m = Math.floor(countdown / 60)
    const s = countdown % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }, [countdown])

  return (
    <div className="node startNode">
      <div className="node-header">Start Node</div>
      <div className="node-content">
        <label>
          <div className="label-text">Tickers</div>
          <input
            type="text"
            value={tickers}
            onChange={handleTickersChange}
            placeholder="SWIGGY,PAYTM.NS,ETHUSDT"
            className="node-input"
          />
        </label>

        <label className="mt-2">
          <div className="label-text">Frequency</div>
          <select
            value={frequency}
            onChange={handleFrequencyChange}
            className="node-input"
          >
            <option value="once">Once</option>
            <option value="1m">1 minute</option>
            <option value="5m">5 minutes</option>
            <option value="15m">15 minutes</option>
            <option value="30m">30 minutes</option>
            <option value="1hr">1 hour</option>
            <option value="custom">Custom</option>
          </select>
        </label>

        {frequency === 'custom' && (
          <label className="mt-2">
            <div className="label-text">Custom (seconds)</div>
            <input
              type="number"
              value={customSeconds}
              onChange={handleCustomSecondsChange}
              min="1"
              className="node-input"
            />
          </label>
        )}

        {/* Countdown display */}
        {frequency !== 'once' && (
          <div className="mt-2 p-2 rounded text-center">
            {isRunning && countdown > 0 ? (
              <>
                <div className="font-medium">Next run in:</div>
                <div className="text-xl font-bold">{formatCountdown()}</div>
              </>
            ) : (
              <div className="font-medium">Waiting to start...</div>
            )}
          </div>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        isConnectable={isConnectable}
      />
    </div>
  )
}

export default StartNode
